import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Users, Trophy, Clock, CheckCircle2, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const MatchCard = ({ match }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  // Check if current user is registered for this match
  const isRegistered = user && (
    match.playerIds?.includes(user.id) || 
    match.players?.some(p => p.userId === user.id || p.id === user.id)
  );
  
  // Check if user is the creator
  const isCreator = user && match.creatorId === user.id;

  const getLevelBadgeColor = (level) => {
    const colors = {
      beginner: isDark ? 'bg-green-500/10 text-green-400 border-green-400/20' : 'bg-green-100 text-green-700 border-green-300',
      intermediate: isDark ? 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20' : 'bg-yellow-100 text-yellow-700 border-yellow-300',
      advanced: isDark ? 'bg-orange-500/10 text-orange-400 border-orange-400/20' : 'bg-orange-100 text-orange-700 border-orange-300',
      pro: isDark ? 'bg-red-500/10 text-red-400 border-red-400/20' : 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[level] || colors.intermediate;
  };

  const getStatusBadge = () => {
    const statusLabels = {
      'open': 'Ouvert',
      'full': 'Complet',
      'pending_payment': 'En attente de paiement',
      'confirmed': 'Confirmé',
      'in_progress': 'En cours',
      'completed': 'Terminé',
      'cancelled': 'Annulé'
    };
    
    const label = statusLabels[match.status] || match.status;
    
    if (match.status === 'open') {
      return (
        <Badge className={isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20' : 'bg-[#0891b2]/10 text-[#0891b2] border-[#0891b2]/30'}>
          {label}
        </Badge>
      );
    }
    if (match.status === 'pending_payment') {
      return (
        <Badge className={isDark ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}>
          {label}
        </Badge>
      );
    }
    if (match.status === 'confirmed' || match.status === 'in_progress') {
      return (
        <Badge className={isDark ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-green-100 text-green-700 border-green-200'}>
          {label}
        </Badge>
      );
    }
    return (
      <Badge className={isDark ? 'bg-zinc-700/50 text-zinc-400 border-zinc-600/20' : 'bg-gray-200 text-gray-600 border-gray-300'}>
        {label}
      </Badge>
    );
  };

  return (
    <div
      onClick={() => navigate(`/matches/${match.id}`)}
      data-testid={`match-card-${match.id}`}
      className={`relative rounded-xl p-6 transition-all duration-200 cursor-pointer border-2 ${
        isRegistered
          ? isDark 
            ? 'bg-[#0f1c2e] border-[#00ff88] ring-2 ring-[#00ff88]/20' 
            : 'bg-white border-[#22c55e] ring-2 ring-[#22c55e]/20 shadow-md'
          : isDark 
            ? 'bg-[#0f1c2e] border-[#1e3a5f] hover:border-[#00d4ff]/50' 
            : 'bg-white border-gray-200 hover:border-[#0891b2]/50 shadow-sm hover:shadow-md'
      } hover:-translate-y-1`}
    >
      {/* Indicateur "INSCRIT" */}
      {isRegistered && (
        <div className={`absolute -top-3 left-4 px-3 py-1 rounded-full flex items-center gap-1.5 text-xs font-bold shadow-lg ${
          isCreator 
            ? isDark 
              ? 'bg-[#c8e600] text-black' 
              : 'bg-[#65a30d] text-white'
            : isDark 
              ? 'bg-[#00ff88] text-black' 
              : 'bg-[#22c55e] text-white'
        }`}>
          <UserCheck size={14} />
          {isCreator ? 'ORGANISATEUR' : 'INSCRIT'}
        </div>
      )}
      
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{match.title}</h3>
          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
            <Clock size={16} />
            <span>
              {format(new Date(match.date), "EEE d MMM 'à' HH:mm", {
                locale: fr,
              })}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {getStatusBadge()}
          {isRegistered && (
            <CheckCircle2 size={20} className={isDark ? 'text-[#00ff88]' : 'text-[#22c55e]'} />
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          <MapPin size={16} className={isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'} />
          <span>{match.location}</span>
        </div>
        <div className="flex items-center gap-2">
          <Users size={16} className={isDark ? 'text-[#00d4ff]' : 'text-[#0891b2]'} />
          <span className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
            {match.players.length} / {match.maxPlayers} joueurs
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Badge className={getLevelBadgeColor(match.requiredLevel)}>
          <Trophy size={14} className="mr-1" />
          {match.requiredLevel}
        </Badge>
        {match.price && (
          <span className={`text-sm font-mono ${isDark ? 'text-[#c8e600]' : 'text-[#65a30d]'}`}>
            {typeof match.price === 'number' ? match.price.toFixed(2) : match.price}€
          </span>
        )}
      </div>
    </div>
  );
};

export default MatchCard;
